import { MouseEvent, ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import { MenuItem } from '@szhsin/react-menu';

import {
  AggressSellProvider,
  AggressBuyProvider,
  useAggressBuyAction,
  useAggressSellAction,
} from '@paradigm/features/src/unified-rfqs/ui/one-click-trade/aggress-action-context';
import {
  RfqMarkPriceProvider,
  useRfqMarkPrice,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-websocket/mark-price-context';
import {
  useRfqQuotesLoader,
  useRfqRowQuotes,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/hooks';
import {
  AggBidProvider,
  AggAskProvider,
  AggGrfqQuote,
  MyAggBidsProvider,
  MyAggAsksProvider,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/quotes-contexts';
import {
  useGrfqRfq,
  useRfqContext,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { bigStone, blackTangaroa } from '@paradigm/design-system/src/colors';
import {
  ArrowDown,
  BaseRow,
  ExpandableContent,
  IdCell,
  MenuContainer,
  useIsRowExpanded,
} from '#/components/rfq-common/TableRow';

import { Grfq } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { useGrfqBbo } from '@paradigm/features/src/unified-rfqs/ui/rfqs/bbo';
import { useQuoteBuilderModalMethods } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/modal-context';

import ExpandableStrategyDescription from '#/components/UnifiedRfqs/components/strategy-description/ExpandableStrategyDescription';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import VerticalDots from '@paradigm/design-system/src/assets/VerticalDots';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import GridCellBase from '#/components/UnifiedRfqs/components/GridCellBase';
import {
  AggressSellField,
  AggressBuyField,
} from '#/components/UnifiedRfqs/one-click-trade/OneClickAction';
import RfqAction from '#/components/UnifiedRfqs/grfq/RfqAction';
import RfqRowBase, {
  ONE_CLICK_ACTION_WIDTH,
  Product,
  RfqType,
  RowIndexProvider,
} from '#/components/UnifiedRfqs/components/RfqRowBase';
import { useRfqSideStoredQty } from '#/components/UnifiedRfqs/one-click-trade/stored-quantity';
import ContextMenuProvider, {
  ContextMenu as BaseContextMenu,
  useContextMenuActions,
  useContextMenuView,
} from '#/components/UnifiedRfqs/context-menu/context-menu';
import { StrategyGreeksRow } from '#/components/UnifiedRfqs/components/StrategyGreeks';
import ScreenPrices from '#/components/UnifiedRfqs/grfq/ScreenPrices';
import { useRfqRowView } from '@paradigm/features/src/unified-rfqs/contexts/rfq-row-view';
import {
  CreationTimeCell,
  LastTradeCell,
} from '#/components/UnifiedRfqs/components/RfqRowCommon';
import MyOrders from '#/components/UnifiedRfqs/grfq/MyOrders';
import CreateMarketMenuItem from '#/components/UnifiedRfqs/context-menu/CreateMarketMenuItem';
import { useSubmitOrderTracker } from '#/components/UnifiedRfqs/one-click-trade/useSubmitOrderTracker';
import { findBaseProductCode } from '@paradigm/features/src/products/domain';

function ContextMenu() {
  const {
    togglePinnedRfq,
    // @TODO: uncomment when implemented proper actions side-effects
    // openRfqDetails,
  } = useContextMenuActions();
  const { isPinnedRfq } = useContextMenuView();
  const { createRfq } = useContextMenuActions();
  const datadogPrefix = 'RFQ Table Grfq Context Menu';
  return (
    <BaseContextMenu>
      <MenuItem onClick={togglePinnedRfq}>
        {isPinnedRfq ? 'Unpin' : 'Pin'}
      </MenuItem>
      <CreateMarketMenuItem
        type="RFQ"
        datadogPrefix={datadogPrefix}
        onClick={() => createRfq('RFQ')}
      />
      <CreateMarketMenuItem
        type="OB"
        datadogPrefix={datadogPrefix}
        onClick={() => createRfq('OB')}
      />
      {/*
      @TODO: uncomment when implemented proper actions side-effects
      <MenuItem onClick={openRfqDetails}>View Details</MenuItem> */}
    </BaseContextMenu>
  );
}

export default function GrfqRow() {
  const { rfqId } = useRfqContext();
  useRfqQuotesLoader(rfqId);
  const { id } = useRfqRowView();
  const { topBidsAgg, topAsksAgg, myBidsAgg, myAsksAgg } =
    useRfqRowQuotes(rfqId);
  const [firstBid, ...remainingBids] = topBidsAgg;
  const [firstAsk, ...remainingAsks] = topAsksAgg;

  const expandedContent = useCallback(
    () => (
      <SecondaryRow data-testid={`rfq-2nd-row-${rfqId}`}>
        <StyledIdCell css="color:white; opacity: 0.5;">
          {id.formatted}
        </StyledIdCell>
        <SecondaryActionsContainer>
          {renderSecondaryRfqActions({
            bids: remainingBids,
            asks: remainingAsks,
          })}
          <ScreenPrices />
        </SecondaryActionsContainer>
        <MyQuotesContainer>
          <MyOrders quoteIndex={1} />
        </MyQuotesContainer>
      </SecondaryRow>
    ),
    [remainingAsks, remainingBids, rfqId, id],
  );

  return withMyQuotes(
    myBidsAgg,
    myAsksAgg,
    <ContextMenuProvider>
      <StyledExpandableContent expandedContent={expandedContent}>
        <AggBidProvider value={firstBid}>
          <AggAskProvider value={firstAsk}>
            <RowIndexProvider value={0}>
              <RfqRowMain />
            </RowIndexProvider>
          </AggAskProvider>
        </AggBidProvider>
      </StyledExpandableContent>
      <ContextMenu />
    </ContextMenuProvider>,
  );
}

// PTP-3858: we should show 3 quotes at all times
const QUOTES_LENGTH = 2;

function renderSecondaryRfqActions({
  bids,
  asks,
}: {
  bids: readonly AggGrfqQuote[];
  asks: readonly AggGrfqQuote[];
}) {
  const actions = [];

  for (let i = 0; i < QUOTES_LENGTH; ++i) {
    actions.push(
      <AggBidProvider key={i} value={bids[i]}>
        <AggAskProvider value={asks[i]}>
          <RowIndexProvider value={i + 1}>
            <OneClickActionContainer>
              <RfqMarkPriceProvider>
                <div css="grid-area: quotes;">
                  <RfqAction isSecondary />
                </div>
              </RfqMarkPriceProvider>
            </OneClickActionContainer>
          </RowIndexProvider>
        </AggAskProvider>
      </AggBidProvider>,
    );
  }

  return actions;
}

const SecondaryActionsContainer = styled.div`
  grid-area: action;
  min-height: 33px;
`;

function RfqRowMain() {
  const grfq = useGrfqRfq();
  const contextMenu = useContextMenuActions();
  const isExpanded = useIsRowExpanded();
  const { open: openQuoteBuilder } = useQuoteBuilderModalMethods();
  const bbo = useGrfqBbo();
  const { type, product, description, productCodes } = useRfqRowView();
  const greeksRow = <StrategyGreeksRow bbo={bbo.data} venue={grfq.venue} />;

  const onContextMenu = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    contextMenu.open({
      anchorPoint: { x: e.clientX, y: e.clientY },
      productCode: findBaseProductCode(productCodes.value),
      rfqId: grfq.id,
    });
  };

  const isMobile = useIsMobile();
  return (
    <RfqRowBase
      onContextMenu={onContextMenu}
      data-testid={`rfq-row-${grfq.id}`}
    >
      {isMobile && (
        <MenuContainer role="cell" data-testid="type" onClick={onContextMenu}>
          <VerticalDots />
        </MenuContainer>
      )}
      <RfqType mobile>{type.formatted}</RfqType>
      <Product mobile area="product" role="cell" data-testid="product">
        {product.formatted}
      </Product>
      <ExpandableStrategyDescription
        onViewAll={() =>
          openQuoteBuilder({ rfqId: grfq.id, side: 'BUY', clickedQuote: null })
        }
        isExpanded={isExpanded}
      >
        {description}
      </ExpandableStrategyDescription>
      <GridCellBase
        area="action"
        mobile
        css={`
          position: relative;
          height: 100%;
        `}
      >
        <RfqMarkPriceProvider>
          <RfqActionWithOneClickTrade rfq={grfq} />
        </RfqMarkPriceProvider>
        <ArrowDown actionWidth={ONE_CLICK_ACTION_WIDTH} />
      </GridCellBase>
      <LastTradeCell />
      {!isMobile && <MyOrders quoteIndex={0} />}
      {greeksRow}
      <CreationTimeCell />
    </RfqRowBase>
  );
}

function withMyQuotes(
  myBidsAgg: AggGrfqQuote[],
  myAsksAgg: AggGrfqQuote[],
  node: ReactNode,
) {
  return (
    <MyAggBidsProvider value={myBidsAgg}>
      <MyAggAsksProvider value={myAsksAgg}>{node}</MyAggAsksProvider>
    </MyAggBidsProvider>
  );
}

function AggressSell() {
  const {
    status,
    quantity,
    quantityDecimals,
    onChangeQuantity,
    isButtonEnabled,
    isFieldEnabled,
    submitAggressQuote,
    lockedQuote,
    resetConfirmState,
  } = useAggressSellAction();
  const { isMarkOrBetter } = useRfqMarkPrice();
  const track = useSubmitOrderTracker({
    auctionType: 'OB',
    side: 'SELL',
    price: lockedQuote?.price,
    isMarkOrBetterFn: isMarkOrBetter,
    status,
  });
  const rfq = useGrfqRfq();

  const onSubmit: typeof submitAggressQuote = useCallback(
    (...args) => {
      track();
      submitAggressQuote(...args);
    },
    [submitAggressQuote, track],
  );

  return (
    <AggressSellField
      isOB
      rfqId={rfq.id}
      price={lockedQuote?.price}
      isFieldEnabled={isFieldEnabled}
      resetConfirmState={resetConfirmState}
      onSubmit={onSubmit}
      quantity={quantity}
      status={status}
      isButtonEnabled={isButtonEnabled}
      onChangeQuantity={onChangeQuantity}
      quantityDecimals={quantityDecimals}
      isMarkOrBetter={isMarkOrBetter}
      productCode={rfq.product_codes[0]}
    />
  );
}

function AggressBuy() {
  const {
    status,
    quantity,
    quantityDecimals,
    onChangeQuantity,
    isButtonEnabled,
    isFieldEnabled,
    submitAggressQuote,
    lockedQuote,
    resetConfirmState,
  } = useAggressBuyAction();
  const { isMarkOrBetter } = useRfqMarkPrice();
  const track = useSubmitOrderTracker({
    auctionType: 'OB',
    side: 'BUY',
    price: lockedQuote?.price,
    isMarkOrBetterFn: isMarkOrBetter,
    status,
  });
  const rfq = useGrfqRfq();

  const onSubmit: typeof submitAggressQuote = useCallback(
    (...args) => {
      track();
      submitAggressQuote(...args);
    },
    [submitAggressQuote, track],
  );

  return (
    <AggressBuyField
      isOB
      rfqId={rfq.id}
      price={lockedQuote?.price}
      isFieldEnabled={isFieldEnabled}
      resetConfirmState={resetConfirmState}
      onSubmit={onSubmit}
      quantity={quantity}
      status={status}
      isButtonEnabled={isButtonEnabled}
      onChangeQuantity={onChangeQuantity}
      quantityDecimals={quantityDecimals}
      isMarkOrBetter={isMarkOrBetter}
      productCode={rfq.product_codes[0]}
    />
  );
}

function RfqActionWithOneClickTrade({ rfq }: { rfq: Grfq }) {
  const storedSell = useRfqSideStoredQty(rfq.id, 'SELL');
  const storedBuy = useRfqSideStoredQty(rfq.id, 'BUY');

  return (
    <OneClickActionContainer>
      <AggressSellProvider {...storedSell}>
        <AggressSell />
      </AggressSellProvider>
      <RfqMarkPriceProvider>
        <RfqAction />
      </RfqMarkPriceProvider>
      <AggressBuyProvider {...storedBuy}>
        <AggressBuy />
      </AggressBuyProvider>
    </OneClickActionContainer>
  );
}

const OneClickActionContainer = styled.div`
  position: relative;
  display: grid;
  justify-content: center;
  align-items: center;
  height: 100%;
  grid-template-columns: 140px 438px 140px;
  grid-template-areas: 'aggbuy quotes aggsell';
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    background: ${blackTangaroa};
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      'quotes quotes'
      'aggbuy  aggsell';
    padding: 0 1rem;
  }
`;

const SecondaryRow = styled(BaseRow)`
  grid-template-columns: 28px 120px 220px ${ONE_CLICK_ACTION_WIDTH}px 70px 430px 60px 80px 89px 42px 42px;
  grid-template-areas: 'type product desc action last mybidask code instrument asset venue time';
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const StyledExpandableContent = styled(ExpandableContent)`
  border-bottom: 1px solid ${bigStone};
`;

const StyledIdCell = styled(IdCell)`
  align-self: end;
  font-size: 11px;
  color: #ffffff;
  opacity: 0.5;
  margin-bottom: 4px;
`;

const MyQuotesContainer = styled.div`
  grid-area: mybidask;
  min-height: 33px;
  font-size: 13px;
  color: #9ca1a7;
  align-self: start;
  padding-top: 8px;
`;
